// NodeViewContent.jsx

import React from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';

// Utilities
import getNodeViewNodes from 'node/views/utils/getNodeViewNodes';
import { getResolvedFields } from 'utilities/NodeType';
import applyCatchAllFacetFilter from 'views/utils/applyCatchAllFacetFilter';
import applyNodeSearchQuery, { getSearchableSchemaFields } from 'views/utils/applyNodeSearchQuery';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';
import { createFilterSortEvaluator } from 'views/utils/filterSortEvaluator';

// Components
import ViewRenderer from 'views/ViewRenderer';

/**
 * NodeViewContent
 *
 * @returns {ReactNode} The rendered component.
 */
const NodeViewContent = React.memo(({
  node,
  childNodes,
  descendantNodes,
  onQuickActionSelect,
  onQuickActionCreateOnly,
  activeTypeKey,
  view,
  searchQuery = '',
  activeCatchAllFacets = [],
  selectedKeys,
  setSelectedKeys,
  tableShowCheckboxes = true,
  onItemClick = null,
}) => {
  // ----- CONTEXT ----- //

  const language = useCurrentLanguage();

  const { getType, getLabel } = useNodeTypes();
  const { user } = useUser();

  const typeKey = activeTypeKey || node?.type || 'NODE';
  const typeDef = getType(typeKey);

  // ----- MEMOS ----- //

  const resolvedProperties = React.useMemo(() => (
    getResolvedFields(typeKey, { includeHidden: true, excludeSecured: true })
  ), [typeKey]);

  const schemaFieldMap = React.useMemo(() => (
    new Map(
      (Array.isArray(resolvedProperties) ? resolvedProperties : []).map((field) => [field?.name || field?.key, field])
    )
  ), [resolvedProperties]);

  const searchableSchemaFields = React.useMemo(() => (
    getSearchableSchemaFields(resolvedProperties)
  ), [resolvedProperties]);

  const resolveFieldValue = React.useCallback((item, fieldDef) => (
    resolveSchemaFieldValue({
      item,
      fieldDef,
      typeKey,
      dataSection: typeDef?.dataSection,
    })
  ), [typeKey, typeDef?.dataSection]);

  const { applyFilters, applySorts } = React.useMemo(() => (
    createFilterSortEvaluator({
      resolveFieldValue,
      schemaFieldMap,
      viewerUserKey: user?.key || null,
    })
  ), [resolveFieldValue, schemaFieldMap, user?.key]);

  const viewNodes = React.useMemo(() => {
    const nodes = getNodeViewNodes({
      activeTypeKey,
      applyFilters,
      applySorts,
      childNodes,
      descendantNodes,
      view,
    });

    const facetFilteredNodes = applyCatchAllFacetFilter(nodes, activeCatchAllFacets, {
      resolveFieldValue,
      schemaFieldMap,
    });

    return applyNodeSearchQuery(facetFilteredNodes, searchQuery, {
      resolveFieldValue,
      searchableSchemaFields,
    });
  }, [
    activeCatchAllFacets,
    activeTypeKey,
    applyFilters,
    applySorts,
    childNodes,
    descendantNodes,
    view,
    searchQuery,
    resolveFieldValue,
    schemaFieldMap,
    searchableSchemaFields,
  ]);

  const addTypeKey = (activeTypeKey && activeTypeKey !== '__HIGHLIGHT__')
    ? activeTypeKey
    : (view?.nodeType || view?.typeKey || null);

  const parentNodeTypeLabel = getLabel(node?.type, language, { fallback: 'Project' });
  // ----- RENDER ----- //

  return (
    <ViewRenderer
      view={view}
      typeDef={typeDef}
      nodes={viewNodes}
      parentNodeKey={node?.key || null}
      parentNodeTypeLabel={parentNodeTypeLabel}
      language={language}
      addTypeKey={addTypeKey}
      selectedKeys={selectedKeys}
      setSelectedKeys={setSelectedKeys}
      tableShowCheckboxes={tableShowCheckboxes}
      onItemClick={onItemClick}
      onQuickActionCreateOnly={onQuickActionCreateOnly}
      onQuickActionSelect={onQuickActionSelect}
    />
  );
});

// Dev Tools
NodeViewContent.displayName = 'NodeViewContent';

export default NodeViewContent;
